<template>
  <div>
    <h1 class="page-title"><b>{{ $t('contract_inputs.contract')}}</b> {{ $t('contract_inputs.information_needed')}}</h1>
    <sc-card class="contract-data">
      <div class="card-title" slot="title">{{ $t('contract_inputs.contracting_details')}}</div>
      <div slot="content" class="sc-content-container">
        <div id="company-input" class="sc-form-input">
          <label for="company-name">{{ $t('contract_inputs.company_name')}}</label>
          <sc-autocomplete
            id="company-name"
            labelProp="$$displayName"
            :options="possibleCompanies"
            v-on:keyDown="onKeyDownCompany($event)"
            @select="onCompanySelect($event)"
            :selected="selectedBisnodeData"
            v-model="contractData.name"
            name="name"
            :class="validationClasses('name')"
            @focus="touch('name')"
            @keyDown="setDirty('name')"
            v-validate="'required'"
            maxlength="255"
          ></sc-autocomplete>
          <div v-if="showCompanySpinner" class="spinner-container">
            <sc-spinner></sc-spinner>
          </div>
          <span v-else class="accepted-input"><i v-if="!errors.has('name') && fields.name && fields.name.valid" class="sc-icon-check"></i><i v-if="errors.has('name')" class="sc-icon-cancel"></i></span>
          <div>
            <span class="validation-error">{{ errors.first('name') }}</span>
          </div>
        </div>

        <div id="tax-input" class="sc-form-input">
          <label for="tax-nbr">{{ $t('contract_inputs.tax_number') }}</label>
          <sc-autocomplete
            id="tax-nbr"
            :labelProp="'taxNbr'"
            :options="possibleTaxNumbers"
            :selected="selectedBisnodeData"
            v-on:keyDown="onKeyDownTax($event)"
            v-on:select="onCompanySelect($event)"
            v-mask="'########-#-##'"
            v-model="contractData.taxNbr"
            name="taxNbr"
            :class="validationClasses('taxNbr')"
            v-validate="'required|min:13'"
            @focus="touch('taxNbr')"
            @keyDown="setDirty('taxNbr')"
            maxlength="50"
          ></sc-autocomplete>
          <div class="spinner-container" v-if="showTaxSpinner">
            <sc-spinner></sc-spinner>
          </div>
          <span v-else class="accepted-input"><i v-if="!errors.has('taxNbr') && fields.taxNbr && fields.taxNbr.valid" class="sc-icon-check"></i><i v-if="errors.has('taxNbr')" class="sc-icon-cancel"></i></span>
          <div>
            <span class="validation-error">{{ errors.first('taxNbr') }}</span>
          </div>
        </div>

        <sc-form-input id="zip-forminput" :class="validationClasses('zip')">
          <label for="zip">{{ $t('contract_inputs.zip')}}</label>
          <input type="text" id="zip" name="zip" v-model="contractData.zip" v-mask="'####'" v-validate="'required|numeric|max:10'" maxlength="10"/>
          <span class="accepted-input"><i v-if="!errors.has('zip') && fields.zip && fields.zip.valid" class="sc-icon-check"></i><i v-if="errors.has('zip')" class="sc-icon-cancel"></i></span>
        </sc-form-input>
        <sc-form-input id="city-forminput" :class="validationClasses('city')">
          <label for="city">{{ $t('contract_inputs.city')}}</label>
          <input type="text" id="city" name="city" v-model="contractData.city" v-validate="'required|max:255'" maxlength="255"/>
          <span class="accepted-input"><i v-if="!errors.has('city') && fields.city && fields.city.valid" class="sc-icon-check"></i><i v-if="errors.has('city')" class="sc-icon-cancel"></i></span>
        </sc-form-input>
        <div class="v-error-container" v-if="errors.has('zip') || errors.has('city')"><span class="validation-error">{{ errors.first('zip') }}</span><span class="validation-error">{{ errors.first('city') }}</span></div>
        <sc-form-input :class="validationClasses('street')">
          <label for="street">{{ $t('contract_inputs.address')}}</label>
          <input type="text" id="street" name="street" v-model="contractData.street" v-validate="'required|max:255'" maxlength="255"/>
          <span class="accepted-input"><i v-if="!errors.has('street') && fields.street && fields.street.valid" class="sc-icon-check"></i><i v-if="errors.has('street')" class="sc-icon-cancel"></i></span>
        </sc-form-input>
        <div>
          <span class="validation-error">{{ errors.first('street') }}</span>
        </div>
        <sc-form-input class="last-name">
          <label for="representativeLastName">{{ $t('contract_inputs.representative_name')}}</label>
          <input type="text" id="representativeLastName" name="representativeLastName" v-model="contractData.representativeLastName" v-validate="'required|max:255'" :placeholder="$t('contract_inputs.last_name')" maxlength="255"/>
          <span class="accepted-input"><i v-if="!errors.has('representativeLastName') && fields.representativeLastName && fields.representativeLastName.valid" class="sc-icon-check"></i><i v-if="errors.has('representativeLastName')" class="sc-icon-cancel"></i></span>
        </sc-form-input>
        <sc-form-input class="first-name">
          <input type="text" id="representative_first" name="representativeFirstName" v-model="contractData.representativeFirstName" v-validate="'required|max:255'" :placeholder="$t('contract_inputs.first_name')" maxlength="255"/>
          <span class="accepted-input"><i v-if="!errors.has('representativeFirstName') && fields.representativeFirstName && fields.representativeFirstName.valid" class="sc-icon-check"></i><i v-if="errors.has('representativeFirstName')" class="sc-icon-cancel"></i></span>
        </sc-form-input>
        <div class="v-error-container" v-if="errors.has('representativeLastName') || errors.has('representativeFirstName')"><span class="validation-error">{{ errors.first('representativeLastName') }}</span><span class="validation-error">{{ errors.first('representativeFirstName') }}</span></div>

        <sc-form-input>
          <label for="representative-phone">{{ $t('contract_inputs.representative_phone')}} <span class="dim">- {{ $t('contract_inputs.optional')}}</span></label>
          <input type="text" id="representative-phone" name="representative-phone" v-model="contractData.representativePhone" :placeholder="$t('contract_inputs.phone_placeholder')" maxlength="255"/>
          <span class="accepted-input"><i v-if="!errors.has('representative-phone') && fields.representativePhone && fields.representativePhone.valid" class="sc-icon-check"></i><i v-if="errors.has('representative-phone')" class="sc-icon-cancel"></i></span>
        </sc-form-input>

        <sc-form-input class="last-name">
          <label for="contactLastName">{{ $t('contract_inputs.contact_name')}}</label>
          <input type="text" id="contactLastName" name="contactLastName" v-model="contractData.contactLastName" v-validate="'required|max:100'" :placeholder="$t('contract_inputs.last_name')" maxlength="100" />
          <span class="accepted-input"><i v-if="!errors.has('contactLastName') && fields.contactLastName && fields.contactLastName.valid" class="sc-icon-check"></i><i v-if="errors.has('contactLastName')" class="sc-icon-cancel"></i></span>
        </sc-form-input>

        <sc-form-input class="first-name">
          <input type="text" id="contactFirstName" name="contactFirstName" v-model="contractData.contactFirstName" v-validate="'required|max:100'" :placeholder="$t('contract_inputs.first_name')" maxlength="100"/>
          <span class="accepted-input"><i v-if="!errors.has('contactFirstName') && fields.contactFirstName && fields.contactFirstName.valid" class="sc-icon-check"></i><i v-if="errors.has('contactFirstName')" class="sc-icon-cancel"></i></span>
        </sc-form-input>
        <div class="v-error-container" v-if="errors.has('contactLastName') || errors.has('contactFirstName')"><span class="validation-error">{{ errors.first('contactLastName') }}</span><span class="validation-error">{{ errors.first('contactFirstName') }}</span></div>

        <sc-form-input>
          <label for="contactPhone">{{ $t('contract_inputs.contact_phone')}}</label>
          <input type="text" id="contactPhone" name="contactPhone" v-model="contractData.contactPhone" :placeholder="$t('contract_inputs.phone_placeholder')" v-validate="'required'" maxlength="20" />
          <span class="accepted-input"><i v-if="!errors.has('contactPhone') && fields.contactPhone && fields.contactPhone.valid" class="sc-icon-check"></i><i v-if="errors.has('contactPhone')" class="sc-icon-cancel"></i></span>
        </sc-form-input>
        <div>
          <span class="validation-error">{{ errors.first('contactPhone') }}</span>
        </div>

        <sc-form-input>
          <label for="contactEmail">{{ $t('contract_inputs.contact_email')}}</label>
          <input type="text" id="contactEmail" name="contactEmail" v-validate="{ required: true, max: 255, validEmail: true, emailExists: true }" v-model="contractData.contactEmail" maxlength="255" />
          <span class="accepted-input"><i v-if="!errors.has('contactEmail') && fields.contactEmail && fields.contactEmail.valid" class="sc-icon-check"></i><i v-if="errors.has('contactEmail')" class="sc-icon-cancel"></i></span>
        </sc-form-input>
        <div>
          <span class="validation-error">{{ errors.first('contactEmail') }}</span>
        </div>

        <div id="invoice-email-label" class="label-w-tooltip">{{ $t('contract_inputs.invoice_email')}}  <sc-tooltip :icon="'info_outline'" class="sc-tooltip" :text="$t('contract_inputs.invoice_email_tooltip')"/></div>
        <sc-checkbox>
          <input type="checkbox" id="contact-mail-checkbox" v-model="contractData.invoiceMailSameAsContact">
          <label class="checkbox-label invoice" for="contact-mail-checkbox">{{ $t('contract_inputs.invoice_email_same_as_contact')}}</label>
        </sc-checkbox>

        <sc-form-input>
          <input v-if="contractData.invoiceMailSameAsContact" :disabled="true" type="text" id="invoiceEmail" name="invoiceEmail" v-model="contractData.contactEmail"  />
          <input v-else type="text" id="invoiceEmail" name="invoiceEmail" v-model="contractData.invoiceEmail" v-validate="{ required: true, max: 255, validEmail: true }" maxlength="255" />
          <span class="accepted-input"><i v-if="!errors.has('invoiceEmail') && fields.invoiceEmail && fields.invoiceEmail.valid" class="sc-icon-check"></i><i v-if="errors.has('invoiceEmail')" class="sc-icon-cancel"></i></span>
        </sc-form-input>
        <div>
          <span class="validation-error">{{ errors.first('invoiceEmail') }}</span>
        </div>
      </div>
    </sc-card>

    <sc-card v-if="longValidationErrors" class="errors-container">
      <div slot="content" class="sc-content-container">
        <ul>
          <li class="long-validation-errors" v-for="error in longValidationErrors" v-bind:key="error">{{ $t(error) }}</li>
        </ul>
      </div>
    </sc-card>

    <div class="button-container">
      <div id="back" class="btn-wrapper">
        <rounded-button color="outline" @click="onBack">{{ $t('common.back')}}</rounded-button>
      </div>
      <div id="next" class="btn-wrapper">
        <rounded-button color="primary" @click="checkForm">{{ $t('common.next')}}</rounded-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ScCard, ScAutocomplete, ScTooltip, ScFormInput, ScCheckbox, ScSpinner } from 'smartcommerce-component-library'
import { mask } from 'vue-the-mask'
import validationMixin from '../../shared/mixins/validationMixin'
import { Routes } from '../utils/Routes'
import _ from 'lodash'
import { OrderWizardMutationTypes as mutations } from '../store/mutation-types'
import { OrderWizardActionTypes as actions } from '../store/action-types'
import { clone } from '../../shared/utils/common'
import RoundedButton from '../../shared/components/RoundedButton'

export default {
  name: 'ContractInput',
  components: {
    ScFormInput,
    ScCard,
    ScAutocomplete,
    ScTooltip,
    ScCheckbox,
    ScSpinner,
    RoundedButton
  },
  directives: { mask },
  mixins: [validationMixin],
  data () {
    return {
      selectedBisnodeData: {},
      longValidationErrors: null,
      showCompanySpinner: false,
      showTaxSpinner: false,
      possibleCompanies: [],
      possibleTaxNumbers: [],
      debouncedCompanySearch: null,
      debouncedTaxNumberSearch: null
    }
  },

  created () {
    if (this.contractData.name && this.contractData.taxNbr) {
      this.selectedBisnodeData = {
        $$displayName: this.contractData.name,
        taxNbr: this.contractData.taxNbr
      }
    }
  },

  methods: {
    async getCompanyDataByName (name) {
      this.showCompanySpinner = true
      const results = await this.$store.dispatch(actions.COMPANY_SEARCH, name)
      this.possibleCompanies = []
      results.forEach(result => {
        result.taxNbr = `${result.taxNbr.substring(0, 8)}-${result.taxNbr.substring(8, 9)}-${result.taxNbr.substring(9)}`
        result.$$displayName = result.shortName || result.name
        this.possibleCompanies.push(result)
      })
      this.showCompanySpinner = false
    },

    async getCompanyDataByTaxNumber (taxNum) {
      this.showTaxSpinner = true
      const results = await this.$store.dispatch(actions.COMPANY_SEARCH, taxNum)
      this.possibleTaxNumbers = []
      results.forEach(result => {
        result.taxNbr = `${result.taxNbr.substring(0, 8)}-${result.taxNbr.substring(8, 9)}-${result.taxNbr.substring(9)}`
        result.$$displayName = result.shortName || result.name
        this.possibleTaxNumbers.push(result)
      })
      this.showTaxSpinner = false
    },

    onKeyDownCompany (value) {
      this.contractData.name = value
      this.selectedBisnodeData = clone(this.selectedBisnodeData || {})
      this.selectedBisnodeData.$$displayName = value
      this.debouncedCompanySearch && this.debouncedCompanySearch.cancel()
      if (value.length >= 3) {
        this.debouncedCompanySearch = _.debounce(() => {
          this.getCompanyDataByName(value)
        }, 300)
        this.debouncedCompanySearch()
      }
    },

    onKeyDownTax (value) {
      this.contractData.taxNbr = value
      this.selectedBisnodeData = clone(this.selectedBisnodeData || {})
      this.selectedBisnodeData.taxNbr = value
      this.debouncedTaxNumberSearch && this.debouncedTaxNumberSearch.cancel()
      if (value.length >= 9) {
        this.debouncedTaxNumberSearch = _.debounce(() => {
          this.getCompanyDataByTaxNumber(value)
        }, 300)
        this.debouncedTaxNumberSearch()
      }
    },

    onCompanySelect (selection) {
      this.selectedBisnodeData = selection
      this.contractData.name = this.selectedBisnodeData.$$displayName
      this.contractData.city = this.selectedBisnodeData.address.city
      this.contractData.zip = this.selectedBisnodeData.address.zip
      this.contractData.street = this.selectedBisnodeData.address.streetNo
      this.contractData.taxNbr = this.selectedBisnodeData.taxNbr
    },

    checkForm () {
      this.setDirty('name')
      this.setDirty('taxNbr')
      this.$validator.validateAll().then(valid => {
        if (!valid) {
          this.longValidationErrors = this.collectLongErrorMessageKeys()
          return
        }
        this.$ga && this.$ga.event({
          eventCategory: 'Order or Purchase',
          eventAction: 'Contact'
        })
        this.$router.push(Routes.PAYMENT)
      })
    },
    onBack () {
      this.$router.push(Routes.VEHICLE_REGISTRATION)
    }
  },
  computed: {
    contractData: {
      get () {
        return this.$store.state.contractData
      },
      set (val) {
        this.$store.commit(mutations.SAVE_CONTRACT_DATA, val)
      }
    }
  },
  watch: {
    contractData: {
      handler: function () {
        this.$store.commit(mutations.SAVE_CONTRACT_DATA, this.contractData)
      },
      deep: true
    },
    'contractData.invoiceMailSameAsContact' (val) {
      if (!val) this.contractData.invoiceEmail = this.contractData.contactEmail
    },
    'contractData.contactEmail' (val) {
      if (this.contractData.invoiceMailSameAsContact) this.contractData.invoiceEmail = this.contractData.contactEmail
    }
  }
}
</script>

<style scoped lang="scss">
@import "../../styles/variables";

.sc-card-container {
  width: 600px;
  box-shadow: 0 0 12px 0 rgba(176, 190, 197, 0.3);
}

.sc-card-title-slot {
  font-size: 18px;
  font-style: normal;
  line-height: 1.33;
  font-weight: $sc-card-title-font-weight;
}

.card-title {
  padding-right: 97px;
  padding-left: 36px;
  padding-top: 20px;
}

.sc-content-container {
  padding: 20px 60px 34px 36px;
}

#tax-input {
  margin-top: 24px;
}

#invoice-email-label {
  font-size: 14px;
  font-weight: 500;
  margin-top: 25px;
}

.label-w-tooltip {
  display: flex;
  align-items: center;
}

.sc-tooltip {
  margin-left: 5px;
  padding-top: 5px;
  user-select: none;

  .sc-tooltip-icon {
    color: rgba(55,71,79,0.3);
  }
}

.checkbox-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: normal;
  font-style: normal;
  color: $sc-dim-text;
  line-height: 1;

  &.invoice {
    font-size: 14px;
    color: $sc-dim-text;
  }
}

.checkboxes {

  .checkbox-label {
    font-size: 16px;

    a {
      font-weight: 600;
      margin: 0 5px;
    }
  }

  input.is-invalid + label::before {
    border-color: $sc-invalid-input-color;
  }
}

.checkbox-label::before {
  width: 20px;
  height: 20px;
}

.dim {
  color: $sc-dim-text;
  opacity: 0.5;
}

label {
  color: #455a64;
  font-size: 14px;
  font-weight: 500;
}

.accepted-input {
  align-self: flex-end;
  align-content: center;
  font-size: 24px;
  text-shadow: 1px 0 $sc-primary;
  color: $sc-primary;
  position: relative;
  float: right;
  height: 40px;
  width: 25px;
  margin-top: -41px;

  .sc-icon-cancel {
    color: $sc-invalid-input-color
  }

}

#company-input {
  margin-top: 0;
}

.sc-form-input {
  width: 480px;

  label {
    color: #2c3e50;
  }

  input {
    width: 430px;
    margin-right: 0;
  }

  &.last-name, &.first-name {
    width: 50%;
    display: inline-block;

    input {
      width: 78.5%;
    }

    &.last-name .accepted-input {
      margin-right: 13px;
    }

    .accepted-input {
      margin-top: 5px;
    }

    +.v-error-container{
      :nth-child(2) {
        margin-left: 35px;
      }

    }
  }
}

#zip-forminput, #city-forminput {
  display: inline-block;
}

#zip-forminput {
  width: 190px;
  margin-right: 15px;

  label {
    letter-spacing: -0.5px;
    width: 100%;
  }
  input {
    width: 150px;
  }

  .accepted-input {
    margin-top: 4px;
  }
}

#city-forminput {
  width: 275px;

  input {
    width: 225px;
  }

  .accepted-input {
    margin-top: 4px;
  }
}

/deep/ .sc-autocomplete-container .sc-autocomplete-input-field {
  width: 430px;
}

.is-invalid {
  border: solid 1px $sc-invalid-input-color;
}

.errors-container {
  margin-top: 36px;
  width: 600px;
  background-color: $sc-validation-error-container-color;
  text-align: left;

  .sc-content-container {
    padding: 0 0 0 10px;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      color: $sc-dim-text;
      font-size: 14px;
      line-height: 1.8;
    }

    li::before {
      content: "!";
      color: $warn;
      font-size: 18px;
      font-weight: 700;
      margin-right: 5px;
    }
  }
}

.v-error-container {

  .validation-error {
    width: 205px;
    display: inline-block;

  }
}

.button-container {
  width: 600px;
  margin: 25px auto;
  display: flex;

  .btn-wrapper {
    width: 50%;
  }

  #back {
    align-self: start;
  }

  #next {
    button {
      margin-left: auto;
    }
  }
}

.spinner-container {
  margin-top: -35px;
  width: 20px;
  height: 20px;
  float: right;
}

@media(max-width: 768px) {
  .page-title{
    margin: 15px 0px;
  }

  .contract-data{
    width: 100%;
  }

  .card-title{
    padding-left: 10px;
  }

  .sc-content-container{
    padding: 10px;
  }

  /deep/ .sc-autocomplete-container .sc-autocomplete-input-field {
    width: 90%;
  }

  .sc-form-input{
    width: 100%;

    input{
      width: 90%;
    }

    &#city-forminput{
      width: 100%;

      input{
        width: 90%;
      }
    }

    &.last-name, &.first-name {
      width: 100%;

      input{
        width: 90%;
      }
    }

    &.last-name.last-name .accepted-input{
      margin-right: 0px;
    }
  }

  .button-container{
    width: 100%;
    justify-content: space-between;

    .btn-wrapper {
      width: 47%;

      button{
        min-width: inherit;
        width: 100%;
      }
    }
  }
}
</style>
